import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { AnyAction } from "redux";
import { IGLMonthlySummary } from "./GLMonthlySummary.types";
import { IStoreState } from "../../initialStoreState";
import { showMessage } from "../../messages/messagesActions";
import { api } from "../../../api/api";
import { translateBackendError } from "../../../helpers/errorTranslator";
import { IBranchOffice } from "../../branchOffice/branchOffice.types";
import { IBranchOption } from "../../../views/Accounting/common/BranchPicker";

export const FETCH_GL_MONTHLY_SUMMARY_LIST_PROGRESS =
  "FETCH_GL_MONTHLY_SUMMARY_LIST_PROGRESS";
export const FETCH_GL_MONTHLY_SUMMARY_LIST_SUCCESS =
  "FETCH_GL_MONTHLY_SUMMARY_LIST_SUCCESS";
export const FETCH_GL_MONTHLY_SUMMARY_LIST_FAILED =
  "FETCH_GL_MONTHLY_SUMMARY_LIST_FAILED";

export const fetchGLMonthlySummaryListProgress = () =>
  action(FETCH_GL_MONTHLY_SUMMARY_LIST_PROGRESS);
export const fetchGLMonthlySummaryListSuccess = (
  data: IGLMonthlySummary[],
  totalRecords: number,
) => action(FETCH_GL_MONTHLY_SUMMARY_LIST_SUCCESS, { data, totalRecords });
export const fetchGLMonthlySummaryListFailed = () =>
  action(FETCH_GL_MONTHLY_SUMMARY_LIST_FAILED);

interface IFetchGLMonthlySummaryListParams {
  fiscal_years_code?: string;
  fiscal_year_month_code?: string;
  gl_account_code?: string;
  year?: string;
  period?: string;
  pageNo: number;
  itemPerPage: number;
  status?: string;
}

export const fetchGLMonthlySummaryListAsync =
  ({
    fiscal_years_code,
    fiscal_year_month_code,
    gl_account_code,
    year,
    period,
    pageNo,
    itemPerPage,
    status,
  }: IFetchGLMonthlySummaryListParams): ThunkAction<
    void,
    IStoreState,
    {},
    AnyAction
  > =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchGLMonthlySummaryListProgress());

      let finalUrl = `/accounting/gl-monthly-summary?pageNo=${pageNo}&itemPerPage=${itemPerPage}`;
      if (fiscal_years_code) {
        finalUrl += `&fiscal_years_code=${fiscal_years_code}`;
      }
      if (fiscal_year_month_code) {
        finalUrl += `&fiscal_year_month_code=${fiscal_year_month_code}`;
      }
      if (gl_account_code) {
        finalUrl += `&gl_account_code=${gl_account_code}`;
      }
      if (year) {
        finalUrl += `&year=${year}`;
      }
      if (period) {
        finalUrl += `&period=${period}`;
      }
      if (status && status !== "-1") {
        finalUrl += `&status=${status}`;
      }

      const res = await api.get(finalUrl);
      const data: IGLMonthlySummary[] = res.data.data;
      dispatch(
        fetchGLMonthlySummaryListSuccess(data, res.data.totalRecords),
      );
    } catch (err: any) {
      dispatch(fetchGLMonthlySummaryListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_GL_MONTHLY_SUMMARY_STATE =
  "CLEAR_GL_MONTHLY_SUMMARY_STATE";
export const clearGLMonthlySummaryState = () =>
  action(CLEAR_GL_MONTHLY_SUMMARY_STATE);

/**
 * Lightweight lookup that fetches the full list of ACTIVE branch offices
 * (backed by `latest_branch_office`) and maps each record to an
 * {@link IBranchOption} for the shared {@link BranchPicker}.
 *
 * This is a callback-only sub-action (no reducer/state) mirroring the existing
 * `fetchBranchOfficeWithCallback` pattern. It reuses the same
 * `/insurance/get-branch-office` endpoint every other branch consumer relies on
 * and intentionally does NOT duplicate the BranchOffice slice — the BranchPicker
 * only needs `{ branch_code, branch_name }`, so it just requests a large page
 * of ACTIVE branches rather than wiring the full BranchOffice list slice.
 */
export const fetchBranchOptionsAsync =
  (
    onCallback: (options: IBranchOption[]) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      // NOTE: `column` is a SEARCH param on this endpoint and must be paired
      // with a `value` ("Both columns and value are required for search.").
      // The BranchPicker needs the full list, not a search, so we mirror the
      // proven list-fetch shape (pageNo/pageLimit/itemPerPage + a wide
      // from_date/to_date window) to reliably return every ACTIVE branch.
      const res = await api.get(
        `/insurance/get-branch-office?pageNo=1&pageLimit=1000&itemPerPage=1000&from_date=1900-01-01&to_date=2100-12-31&status=ACTIVE`,
      );
      const data: IBranchOffice[] = res.data.data ?? [];
      const options: IBranchOption[] = data
        .filter((branch) => Boolean(branch.branch_code))
        .map((branch) => ({
          branch_code: branch.branch_code,
          branch_name: branch.name || undefined,
        }));
      onCallback(options);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    }
  };
